/**
 * Wind-barb glyph classing for the interactive map's `barbs` vector layer.
 *
 * Meteorological convention: glyphs are classed in KNOTS (half pip = 5 kt,
 * full pip = 10 kt, flag = 50 kt) regardless of the display unit, which is
 * why the backend ships a `kt` property alongside u/v — see backend
 * `rendering/vector.py`.
 *
 * Only the pure classing lives here (shared by the webapp's canvas-drawn
 * glyphs and the mobile app's pre-rendered assets); the drawing itself is
 * platform-specific and stays out of core.
 */

import type {ForecastMapParameters} from '../../api/types';

// Glyph classes: 0 (calm circle), 5, 10, ... up to this cap.
export const BARB_MAX_KNOTS = 100;
export const BARB_STEP_KNOTS = 5;

// Glyph ink variants: dark line art for pale backgrounds (plain basemap,
// scalar fills), white for the saturated wind-speed raster — chosen by
// windSpeedRasterPainted below.
export type BarbVariant = 'dark' | 'white';

const variantPrefix = (variant: BarbVariant): string =>
  variant === 'white' ? 'wind-barb-white-' : 'wind-barb-';

export const barbIconName = (
  knots: number,
  variant: BarbVariant = 'dark',
): string => {
  const bucket = Math.min(
    BARB_MAX_KNOTS,
    Math.max(0, Math.round(knots / BARB_STEP_KNOTS) * BARB_STEP_KNOTS),
  );
  return `${variantPrefix(variant)}${bucket}`;
};

// The style-expression mirror of barbIconName for the symbol layer's
// icon-image: floor/ceil-free bucketing via round(kt / 5) * 5, clamped.
const barbIconExpressionFor = (variant: BarbVariant) =>
  [
    'concat',
    variantPrefix(variant),
    [
      'to-string',
      [
        'min',
        BARB_MAX_KNOTS,
        [
          'max',
          0,
          [
            '*',
            BARB_STEP_KNOTS,
            ['round', ['/', ['get', 'kt'], BARB_STEP_KNOTS]],
          ],
        ],
      ],
    ],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ] as any;

export const barbIconExpression = barbIconExpressionFor('dark');
export const whiteBarbIconExpression = barbIconExpressionFor('white');

// True when the config's paintable layer is the 10m-wind magnitude fill —
// i.e. the interactive overlay under the barbs/particles is the saturated
// wind-speed raster, where white line art reads. Everywhere else (plain
// basemap, scalar fills like temperature) dark ink does.
export const windSpeedRasterPainted = (
  parameters: ForecastMapParameters | null | undefined,
): boolean =>
  parameters?.layers.some(
    (layer) =>
      layer.layer_type === 'magnitude' &&
      layer.u_metric === '10u' &&
      layer.v_metric === '10v',
  ) ?? false;
